import FlexSearch from 'flexsearch';
import { FreeSearchField } from '../../../modules/generic/free-search-field';
import cloneDeep from 'lodash/cloneDeep';
import analytics from '../../analytics';

class CarSearchFreeText {
  /**
   * Ajax endpoint where we can get fresh data
   *
   * @type {string}
   * @private
   */
  #searchItemsAjaxEndPoint = '/stage-ajax/car-search-items/';

  /**
   * Session storage name where data from server will
   * be stored. If modifying data structure, please
   * add one number more to vX
   *
   * @type {string}
   * @private
   */
  #sessionStorageName = 'carSearchIndexStore_v4';

  /**
   * searchValues contains all items that is searchable
   * Basically value will be assigned from server or from
   * sessionStorage
   *
   * @type {object[]}
   * @private
   */
  #searchValues = [];

  /**
   * Free text search input. Filled in constructor
   *
   * @type {HTMLElement|null}
   */
  #freeTextSearchInput = null;

  /**
   * Free Search Field instance
   *
   * @type {object}
   */
  #freeSearchFieldInstance = null;

  /**
   * Search synonyms array of objects
   *
   * @type {object[]}
   */
  #searchSynonyms = [];
  /**
   * Constructor of CarSearchFreeText
   * It will load items either from sessionStorage
   * or from server if session storage does not exists
   * @param {object} carSearchInstance car search instance
   */
  constructor(carSearchInstance) {
    this.searchIndex = new FlexSearch.Index({
      profile: 'score',
      tokenize: 'full',
      charset: 'latin:advanced'
    });

    // Assign car search instance to carSearch member
    this.carSearch = carSearchInstance;

    // Initialize FreeSearchField
    this.#freeSearchFieldInstance = new FreeSearchField(
      this.carSearch.element.querySelector('.free-search-field')
    );

    // Get synonyms from window-object
    try {
      this.#searchSynonyms = JSON.parse(window.searchSynonyms);
    } catch(e) {
      console.warn('Could not parse synonyms!', e);
    }

    // Bind event
    this.#bindEvents();

    // Get free-text search input from car search element
    this.#freeTextSearchInput = this.carSearch.element.querySelector('.free-search-field-container input[type="text"]') || null;

    // Load items and then add items to FlexSearch index
    this.#loadItems().then(data => {
      if(data) {
        this.#indexItems(data);
      } else {
        console.error('Could not index data to FlexSearch. Data was invalid.');
      }
    });
  }


  /**
   * Result click event
   *
   * @param {HTMLElement} mainLinkElement - Clicked result link
   * @async
   */
  async resultLinkClickEvent(mainLinkElement) {
    if (mainLinkElement) {
      const searchData = JSON.parse(mainLinkElement.dataset.searchdata);
      const arrayToSearch = [];



      // Add orderable category filter to searchData if orderable tab is active
      if (this.carSearch.isOrderableTabActive) {
        searchData.selectedParams['category[]'] = 'tilattavat-autot';
      }

      // Get input value for dataLayer
      const inputValue = this.#freeTextSearchInput?.value || null;

      // Push selectedParams to dataLayer
      analytics.pushToDataLayer({
        event: 'UI Interaction',
        interaction: {
          elementName: 'UI - Car Search - Free Text Search Result',
          selectedItem: searchData.selectedParams,
          type: 'click',
          textInputValue: inputValue,
        }
      });

      // Set car search isLoading to true
      this.carSearch.toggleLoadingAnimation = true;
      for (const [key, value] of Object.entries(searchData.selectedParams)) {

        // Because models depends on what brand is selected
        // We need to add special condition to load models
        // from server.
        if (key === 'brands[]') {
          const activeKey = this.carSearch.isOrderableTabActive ? `${key}--orderable` : key;
          window.multiselects[activeKey].selectOption(window.multiselects[activeKey].options.find(o => o.value === value));

          // If search item contains models (user has clicked e.g. BMW 118)
          // Get models from server with model preselected
          if (searchData.selectedParams['models[]']) {
            await this.carSearch.handleModelDropdown(false, [searchData.selectedParams['models[]']]);
          } else {
            // No models parameter exists, we do not have to
            // wait models to load.
            this.carSearch.handleModelDropdown();
          }
        } else {
          const activeKey = this.carSearch.isOrderableTabActive ? `${key}--orderable` : key;
          const shownValue = document.querySelector(`[name="${activeKey}"][value="${value}"], [data-select-name="${activeKey}"] [data-value="${value}"]`)?.dataset?.shownValue;
          arrayToSearch.push({
            name: activeKey,
            value: value,
            text: shownValue
          });
        }

      }
      // Do car search
      this.doSearch(arrayToSearch);
    }
  }

  /**
   * Do FlexSearch string search
   *
   * @param {string} searchString - Search string
   * @param {number} resultAmount - Number of results
   * @returns {object[]}
   * @async
   */
  async doFreeTextSearch(searchString, resultAmount = 5) {
    // Execute search function in FlexSearch
    const resultIds = await this.searchIndex.search(searchString, {
      limit: resultAmount,
      suggest: false
    });

    const returnValue = [];

    for (const id of resultIds) {
      // FlexSearch returns only IDs, so we need to
      // push results from this.#searchValues by ID
      const result = this.#searchValues[id];

      // Parse GET-parameters
      const getParameters = new URLSearchParams(new URL(result.link).search);
      const selectedParams = {};

      // Add GET-parameters according to result
      for (const [key, value] of getParameters) {
        selectedParams[key] = value;
      }

      returnValue.push({
        ...result,
        selectedParams
      });
    }

    // Make deep clone of return values
    // Weird things happened if returnValue
    // is not deep cloned.
    return cloneDeep(returnValue);
  }

  /**
   * Execute car search after manipulating facets
   *
   * @param {object[]} newFilters - New filters from free text search searchParams
   */
  doSearch(newFilters) {
    const currentFilters = this.carSearch.getSelectedFilters();

    for (const filter of newFilters) {
      if (!currentFilters.filter((item) => item?.name === filter.name && item?.value === filter.value).length) {
        currentFilters.push(filter);
      }
    }

    this.carSearch.manipulateSearchParameters(currentFilters, { freeTextSearch: true });
    this.carSearch.searchCars(false, { doNotHandleModelDropdown: true });
  }

  /**
   * Bind events to free text field
   */
  #bindEvents() {
    const freeTextField = this.carSearch.element.querySelector('.free-search-field');

    // Listen event when user writes something to the input field
    freeTextField.addEventListener('freeTextFieldInput', async (e) => {
      const searchString = this.#checkAndReplaceSynonyms(e.detail.value);
      // Do FreeTextSearch via FlexSearch
      const results = await this.doFreeTextSearch(searchString);
      // Render data via FreeSearch field instance
      this.#freeSearchFieldInstance.renderResults(e.detail.value, results);
    });

    // Listen event when user clicks result from free text field results area
    freeTextField.addEventListener('freeTextFieldResultClick', async (e) => {
      this.resultLinkClickEvent(e.detail.linkElement);
    })
  }

  /**
   * Find synonyms in search string and replace it with meaning word
   *
   * @param {string} searchString - Current search string
   * @returns {string} Replaced search string or original
   * @private
   */
  #checkAndReplaceSynonyms(searchString) {
    if(searchString.length) {
      const replacedSearchString = searchString.split(' ').map(word => {
        // Do not match empty string
        if(!word.length) {
          return word;
        }

        const foundWord = this.#searchSynonyms.find(o => o.synonym_word.toLowerCase().includes(word.toLowerCase()));
        return foundWord ? foundWord.meaning_word : word;
      }).join(' ');
      return replacedSearchString;
    }
    return searchString;
  }

  /**
   * Add items to FlexSearch index
   *
   * @param {object[]} indexData - Data to add FlexSearch index
   * @private
   */
  #indexItems(indexData) {
    this.#searchValues = indexData;
    for (const item in indexData) {
      indexData[item].id = item;
      this.searchIndex.add(indexData[item].id, indexData[item].keywords);
    }
  }

  /**
   * Load items either from sessionStorage or server
   * If sessionStorage does not exists, load fresh data
   * from server.
   *
   * @returns {object[]|null} - Data from server or from sessionStorage or null if server request fails
   * @private
   * @async
   */
  async #loadItems() {
    let dataFromSessionStorage = null;
    if(window.sessionStorage?.getItem(this.#sessionStorageName)) {
      // Try parse session storage
      try {
        // If couldn't parse, keep dataFromSessionStorage null to
        // indicate that we have to get fresh data from server
        dataFromSessionStorage = JSON.parse(window.sessionStorage.getItem(this.#sessionStorageName));
        return dataFromSessionStorage;
      } catch(e) {
        console.log('JSON data invalid in sessionStorage, try get fresh data from server');
      }
    }

    // sessionStorage data does not exists or data is corrupted
    // Get fresh data from server
    if(!dataFromSessionStorage) {
      return await fetch(this.#searchItemsAjaxEndPoint)

      // Convert response to JSON
        .then(response => response.json())

      // Do magic, add data to sessionStorage
        .then(response => {
          sessionStorage.setItem(this.#sessionStorageName, JSON.stringify(response));
          return response;
        })

      // Catch errors, returns null
        .catch(error => {
          console.log(`Could not get data from server correctly. ${error}`);
          return null;
        });
    }
  }
}

export default CarSearchFreeText;
